import { BLUEPRINT_LEVELS } from './blueprint-levels.ts';
import { MINECART_LEVELS } from './minecart-levels.ts';
import { ADVENTURES } from './expedition-adventures.ts';
import { REDSTONE_LEVELS } from './redstone-levels.ts';

/**
 * Website-only cosmetics. They never appear in Minecraft or change the family's world.
 * Each is granted once, by the server, from verified progress (never from a client claim).
 */
export type Slot = 'badge' | 'frame' | 'emblem' | 'trophy';
export type Medal = 'solved' | 'gold';

/** What the server knows about one kid's Fun progress. */
export interface FunProgress {
  /** Best medal per solved level, per activity. */
  solved: Partial<Record<ActivityId, Record<string, Medal>>>;
  /** Spider-hunt finds reported by the browser (unverified, so only a welcome badge). */
  spiderFinds: number;
  /** Skin Studio designs ever created (a creative milestone, never an art score). */
  skinsCreated?: number;
  /** Wrong Build Blueprint checks per level since it was last solved (decides the gold medal). */
  blueprintMisses?: Record<string, number>;
}

export type ActivityId = 'redstone' | 'blueprint' | 'minecarts' | 'expedition';

export interface RewardItem {
  id: string;
  slot: Slot;
  name: string;
  /** Shown before it is earned, so kids know what to aim for. */
  unlock: string;
  glyph:
    | 'spider'
    | 'circuit'
    | 'lamp'
    | 'trophy'
    | 'palette'
    | 'blueprint'
    | 'cart'
    | 'rail'
    | 'backpack'
    | 'compass';
  accent: 'red' | 'gold' | 'green' | 'purple';
  /** Where to earn it (a Fun activity, or the spider hunt on Lore). */
  href: string;
  /** How far along the kid is ({have} is capped at {need}). */
  progress: (p: FunProgress) => { have: number; need: number };
  /** Derived from progress: have >= need. */
  earned: (p: FunProgress) => boolean;
}

const solvedCount = (p: FunProgress, a: ActivityId) => Object.keys(p.solved[a] ?? {}).length;
const goldCount = (p: FunProgress, a: ActivityId) =>
  Object.values(p.solved[a] ?? {}).filter((m) => m === 'gold').length;

const upTo = (have: number, need: number) => ({ have: Math.min(have, need), need });

type RewardDef = Omit<RewardItem, 'earned'>;
const DEFS: RewardDef[] = [
  {
    id: 'badge-artist',
    slot: 'badge',
    name: 'Artist',
    unlock: 'Save your first design in Skin Studio.',
    glyph: 'palette',
    accent: 'purple',
    href: '/fun/skin-studio',
    progress: (p) => upTo(p.skinsCreated ?? 0, 1),
  },
  {
    id: 'frame-palette',
    slot: 'frame',
    name: 'Palette frame',
    unlock: 'Save 3 different designs in Skin Studio.',
    glyph: 'palette',
    accent: 'gold',
    href: '/fun/skin-studio',
    progress: (p) => upTo(p.skinsCreated ?? 0, 3),
  },
  {
    id: 'badge-spider-spotter',
    slot: 'badge',
    name: 'Spider Spotter',
    unlock: 'Find a hidden spider in the bonus spider hunt.',
    glyph: 'spider',
    accent: 'red',
    href: '/lore',
    progress: (p) => upTo(p.spiderFinds, 1),
  },
  {
    id: 'badge-engineer',
    slot: 'badge',
    name: 'Engineer',
    unlock: 'Solve 3 Redstone Relay puzzles.',
    glyph: 'lamp',
    accent: 'gold',
    href: '/fun/redstone',
    progress: (p) => upTo(solvedCount(p, 'redstone'), 3),
  },
  {
    id: 'emblem-circuit',
    slot: 'emblem',
    name: 'Circuit emblem',
    unlock: `Solve all ${REDSTONE_LEVELS.length} Redstone Relay puzzles.`,
    glyph: 'circuit',
    accent: 'red',
    href: '/fun/redstone',
    progress: (p) => upTo(solvedCount(p, 'redstone'), REDSTONE_LEVELS.length),
  },
  {
    id: 'frame-redstone',
    slot: 'frame',
    name: 'Redstone frame',
    unlock: 'Earn 3 gold medals in Redstone Relay (use few pieces).',
    glyph: 'circuit',
    accent: 'gold',
    href: '/fun/redstone',
    progress: (p) => upTo(goldCount(p, 'redstone'), 3),
  },
  {
    id: 'trophy-redstone-gold',
    slot: 'trophy',
    name: 'Golden Relay trophy',
    unlock: 'Earn a gold medal on every Redstone Relay puzzle.',
    glyph: 'trophy',
    accent: 'gold',
    href: '/fun/redstone',
    progress: (p) => upTo(goldCount(p, 'redstone'), REDSTONE_LEVELS.length),
  },
  {
    id: 'badge-builder',
    slot: 'badge',
    name: 'Builder',
    unlock: 'Build 3 Build Blueprint models.',
    glyph: 'blueprint',
    accent: 'green',
    href: '/fun/blueprints',
    progress: (p) => upTo(solvedCount(p, 'blueprint'), 3),
  },
  {
    id: 'frame-blueprint',
    slot: 'frame',
    name: 'Blueprint frame',
    unlock: `Build all ${BLUEPRINT_LEVELS.length} Build Blueprint models.`,
    glyph: 'blueprint',
    accent: 'purple',
    href: '/fun/blueprints',
    progress: (p) => upTo(solvedCount(p, 'blueprint'), BLUEPRINT_LEVELS.length),
  },
  {
    id: 'badge-conductor',
    slot: 'badge',
    name: 'Conductor',
    unlock: 'Solve 4 Minecart Switchyard puzzles.',
    glyph: 'cart',
    accent: 'green',
    href: '/fun/minecarts',
    progress: (p) => upTo(solvedCount(p, 'minecarts'), 4),
  },
  {
    id: 'emblem-rail',
    slot: 'emblem',
    name: 'Rail emblem',
    unlock: `Solve all ${MINECART_LEVELS.length} Minecart Switchyard puzzles.`,
    glyph: 'rail',
    accent: 'gold',
    href: '/fun/minecarts',
    progress: (p) => upTo(solvedCount(p, 'minecarts'), MINECART_LEVELS.length),
  },
  {
    id: 'badge-explorer',
    slot: 'badge',
    name: 'Explorer',
    unlock: 'Complete 3 Expedition Loadout adventures.',
    glyph: 'backpack',
    accent: 'green',
    href: '/fun/expedition',
    progress: (p) => upTo(solvedCount(p, 'expedition'), 3),
  },
  {
    id: 'emblem-compass',
    slot: 'emblem',
    name: 'Compass emblem',
    unlock: `Complete all ${ADVENTURES.length} Expedition Loadout adventures.`,
    glyph: 'compass',
    accent: 'gold',
    href: '/fun/expedition',
    progress: (p) => upTo(solvedCount(p, 'expedition'), ADVENTURES.length),
  },
];

export const REWARDS: RewardItem[] = DEFS.map((d) => ({
  ...d,
  earned: (p) => {
    const { have, need } = d.progress(p);
    return have >= need;
  },
}));

export const rewardById = (id: string) => REWARDS.find((r) => r.id === id);

/** Rewards still to earn (not granted, not done), closest to done first (ties keep list order). */
export function nextToEarn(p: FunProgress, granted: ReadonlySet<string>): RewardItem[] {
  const share = (r: RewardItem) => {
    const { have, need } = r.progress(p);
    return have / need;
  };
  return REWARDS.filter((r) => !granted.has(r.id) && !r.earned(p))
    .map((r, i) => ({ r, i, f: share(r) }))
    .sort((a, b) => b.f - a.f || a.i - b.i)
    .map((x) => x.r);
}

/** Rewards the progress earns that are not granted yet. */
export const newlyEarned = (p: FunProgress, granted: ReadonlySet<string>) =>
  REWARDS.filter((r) => !granted.has(r.id) && r.earned(p));

/** Equippable slots (trophies sit on the shelf). */
export const EQUIP_SLOTS = ['badge', 'frame', 'emblem'] as const;
export type EquipSlot = (typeof EQUIP_SLOTS)[number];

/** Activities shown in the Fun hub. Unfinished activities are not listed. */
export const ACTIVITIES: {
  id: string;
  title: string;
  blurb: string;
  href: string;
  /** Puzzle activities: how many levels (hub shows "n of count solved"). */
  count?: number;
}[] = [
  {
    id: 'redstone' as const,
    title: 'Redstone Relay',
    blurb: 'Wire up power to light the lamps — and keep the red ones dark.',
    href: '/fun/redstone',
    count: REDSTONE_LEVELS.length,
  },
  {
    id: 'skin',
    title: 'Skin Studio',
    blurb: 'Paint your own Minecraft skin, see it in 3D and download it.',
    href: '/fun/skin-studio',
  },
  {
    id: 'blueprint' as const,
    title: 'Build Blueprint',
    blurb: 'Study a little model from every side, then build it block by block on your plot.',
    href: '/fun/blueprints',
    count: BLUEPRINT_LEVELS.length,
  },
  {
    id: 'minecarts' as const,
    title: 'Minecart Switchyard',
    blurb: 'Set the switches, press Run, and get every cart to its depot without a crash.',
    href: '/fun/minecarts',
    count: MINECART_LEVELS.length,
  },
  {
    id: 'expedition' as const,
    title: 'Expedition Loadout',
    blurb: 'Pack your backpack for an adventure, then choose how to use what you brought.',
    href: '/fun/expedition',
    count: ADVENTURES.length,
  },
];
